// ============================================================
// ANSICHTSSACHE-AGENT – Kaestchen
// ============================================================
// Das native Kaestchen ist in jedem Browser ein anderes: Chrome
// zeichnet seit Kurzem eine zwei Pixel starke graue Kante mit
// eigenen runden Ecken, Safari ein flaches Quadrat, und
// `accent-accent` faerbt davon nur die Fuellung — die Kante
// bleibt in beiden Faellen Systemgrau. Neben einem Eingabefeld
// mit einer Pixelkante und dem Radius aus den Tokens sah das aus
// wie hineingefallen. Darum `appearance-none` und ein eigener
// Haken: dieselbe Kante, derselbe Radius, derselbe Fokusring wie
// beim Feld nebenan, und im Dunkelmodus faellt nichts auf die
// Systemfarben zurueck.
//
// Der zweite Fehler steckte im Layout. Das Kaestchen war
// `inline-flex`, und Inline-Elemente stapeln sich in einer Spalte
// mit `space-y-3` nicht — sie laufen in derselben Zeile weiter
// und kleben aneinander („Bekommt die WeihnachtskarteBekommt den
// Newsletter"). Jetzt traegt die beschriftete Fassung `flex`,
// damit sie von selbst stapelt, dazu `w-fit`, damit die
// Klickflaeche am Text endet: ein Klick weit rechts daneben soll
// nichts umschalten.
//
// `hint` steht unter der Beschriftung an DEREN Textkante. Vorher
// setzten die Masken diesen Satz als eigenes <p> darunter, also
// linksbuendig zum Kaestchen — er las sich wie ein neuer Absatz
// und nicht wie die Erklaerung des Hakens darueber.
//
// OHNE `label` rendert das Bauteil nur das Kaestchen, kein
// <label>. Das ist kein Sonderfall, sondern der haeufigere: in
// Listen traegt die ganze Zeile die Beschriftung und ist selbst
// schon ein <label>. Ein Label im Label ist ungueltiges HTML —
// der Klick zaehlte dort doppelt und hob sich auf.
// ============================================================

import { forwardRef } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  /** Die Erklaerung zum Haken — steht an der Textkante der Beschriftung. */
  hint?: React.ReactNode;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, hint, id, ...props }, ref) => {
    const checkboxId = id || props.name;
    const beschriftet = Boolean(label || hint);

    // Das Kaestchen sitzt auf der ERSTEN Textzeile: 16 Pixel hoch
    // in einer 18 Pixel hohen Zeile, also einen Pixel tiefer. Bei
    // zweizeiliger Beschriftung bleibt es dort oben stehen, statt
    // in die Mitte des Blocks zu rutschen.
    const kaestchen = (
      <span
        className={cn(
          // `inline-flex`, nicht `flex`: ohne Beschriftung steht das
          // Kaestchen auch mal im Fliesstext (Aufgabenliste einer
          // Notiz), und ein Blockelement braeche dort die Zeile um.
          // Als Kind eines Flex-Containers macht es keinen
          // Unterschied — dort blockifiziert der Browser ohnehin.
          "relative inline-flex h-4 w-4 shrink-0 items-center justify-center",
          beschriftet ? "mt-px" : className,
        )}
      >
        <input
          id={checkboxId}
          type="checkbox"
          ref={ref}
          className={cn(
            "peer bg-bg-primary border-border-control h-4 w-4 appearance-none rounded-[4px] border",
            "duration-fast ease-standard transition-[background-color,border-color,box-shadow]",
            // Zweimal dasselbe mit Absicht: `hover` fuer das
            // Kaestchen selbst, `group-hover` fuer den Fall, dass
            // die Beschriftung daneben ueberfahren wird.
            "hover:border-accent/60 group-hover:border-accent/60",
            "checked:border-accent checked:bg-accent",
            "checked:hover:border-accent-hover checked:hover:bg-accent-hover",
            "group-hover:checked:border-accent-hover group-hover:checked:bg-accent-hover",
            // Kein Ring-Offset: bei einem 16-Pixel-Kaestchen
            // saesse sonst ein weisser Kragen darum, der auf
            // grauem Grund als Kasten mitliest.
            "focus-visible:ring-accent/30 focus-visible:ring-[3px] focus-visible:outline-none",
            "disabled:border-border-control disabled:cursor-not-allowed disabled:opacity-40",
          )}
          {...props}
        />
        {/* Der Haken wird eingeblendet und waechst dabei auf seine
            Groesse — ohne das Wachsen wirkt das Umschalten hart,
            mit mehr davon albern. */}
        <Check
          aria-hidden
          strokeWidth={3.5}
          className={cn(
            "text-accent-contrast pointer-events-none absolute h-3 w-3 scale-75 opacity-0",
            "duration-fast ease-standard transition-[opacity,transform]",
            "peer-checked:scale-100 peer-checked:opacity-100",
          )}
        />
      </span>
    );

    if (!beschriftet) return kaestchen;

    return (
      <label
        htmlFor={checkboxId}
        className={cn(
          "group flex w-fit max-w-full items-start gap-2.5",
          props.disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
          className,
        )}
      >
        {kaestchen}
        <span className="min-w-0">
          {label && <span className="text-body-sm block select-none">{label}</span>}
          {hint && (
            <span className="text-caption text-text-tertiary mt-0.5 block select-none">{hint}</span>
          )}
        </span>
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
